import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { PublicQuoteView } from '@catalyst-psa/shared';

import { publicApi } from '../project-portal/publicApi.js';
import { QuoteDeliveryView } from './QuoteDeliveryView.js';

/**
 * View-only "SOW for delivery team" page (/sow/:token) — unauthenticated, token is the
 * only credential. Renders the same document as the customer delivery page but without
 * the approval form or option toggles, and never records a visit.
 */
export function PublicQuoteSowPage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const query = useQuery({
    queryKey: ['public-quote-sow', token],
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => publicApi.get<PublicQuoteView>(`/public/quotes/sow/${token}`),
  });

  if (query.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p className="text-sm text-slate-500">Loading quote…</p>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-slate-800">Quote unavailable</h1>
          <p className="text-sm text-slate-500">
            This link is invalid or has been disabled. Ask the quote owner to generate a new
            &ldquo;SOW for delivery team&rdquo; link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="mx-auto max-w-3xl px-4">
        <QuoteDeliveryView view={query.data} readOnly />
        <p className="mt-4 text-center text-xs text-slate-400">
          View-only copy for the Catalyst delivery team.
        </p>
      </div>
    </div>
  );
}
